import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamMembers = [
    {
      name: 'Marcus Chen',
      role: 'Founder & CEO',
      bio: 'Former logistics manager with 15 years in regional distribution. Built Sonoma Routes to serve the independent retail community.',
      image: 'https://public.readdy.ai/ai/img_res/e446633f1030a8aaac8f8113e62776b0.jpg'
    },
    {
      name: 'Sarah Mitchell',
      role: 'Operations Director',
      bio: 'Expert in route optimization and inventory management. Ensures every delivery runs on schedule.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20friendly%20Caucasian%20woman%20in%20her%2030s%20with%20brown%20hair%20wearing%20a%20simple%20white%20blouse%20against%20a%20clean%20neutral%20gray%20background%20with%20soft%20natural%20lighting%20and%20professional%20corporate%20photography%20style&width=400&height=400&seq=team-sarah-001&orientation=squarish'
    },
    {
      name: 'David Rodriguez',
      role: 'Producer Relations',
      bio: 'Connects local producers with retail opportunities. Passionate about supporting Sonoma County makers.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20friendly%20Hispanic%20American%20male%20in%20his%2030s%20with%20short%20dark%20hair%20wearing%20a%20casual%20gray%20polo%20shirt%20against%20a%20clean%20neutral%20gray%20background%20with%20soft%20natural%20lighting%20and%20professional%20corporate%20photography%20style&width=400&height=400&seq=team-david-001&orientation=squarish'
    },
    {
      name: 'Emily Nakamura',
      role: 'Route Manager',
      bio: 'Manages daily operations across all Sonoma County routes. Keeps shelves stocked and stores happy.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Asian%20American%20woman%20in%20her%20late%2020s%20with%20long%20black%20hair%20wearing%20a%20dark%20green%20sweater%20against%20a%20clean%20neutral%20gray%20background%20with%20soft%20natural%20lighting%20and%20professional%20corporate%20photography%20style&width=400&height=400&seq=team-emily-001&orientation=squarish'
    }
  ];

  const milestones = [
    { year: '2019', title: 'Founded', description: 'Started with 5 stores and a single route in Santa Rosa.' },
    { year: '2020', title: 'Expanded', description: 'Grew to 25 stores across central Sonoma County.' },
    { year: '2022', title: 'Producer Network', description: 'Launched partnerships with 15 local producers.' },
    { year: '2024', title: 'County-Wide', description: 'Now serving 80+ stores throughout Sonoma County.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Built for Sonoma County, by Sonoma County.
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We're a local distribution company dedicated to connecting independent retailers with quality snack products. No middlemen, no complexity—just reliable service.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                From One Route to County-Wide Coverage
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed">
                <p>
                  Sonoma Routes started in 2019 with a simple observation: independent stores in Sonoma County were underserved by traditional distributors. Big companies wanted big orders. Small stores got left behind.
                </p>
                <p>
                  Our founder, Marcus Chen, spent 15 years in regional logistics before deciding to build something different. A distribution model designed specifically for independent retailers—one that starts small, proves what sells, and scales from there.
                </p>
                <p>
                  Today, we serve over 80 stores across Sonoma County with weekly and bi-weekly restocking routes. We work with local producers who make quality products but lack the infrastructure to reach retail shelves. And we help store owners stock products that actually move.
                </p>
                <p>
                  Our approach is simple: small initial placements, consistent service, and data-driven reordering. No pressure to over-order. No products sitting on shelves. Just a reliable partnership that works for everyone.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20delivery%20van%20parked%20outside%20a%20charming%20independent%20retail%20store%20in%20wine%20country%20California%20with%20rolling%20hills%20in%20background%20showing%20local%20small%20business%20distribution%20and%20community%20commerce%20with%20warm%20natural%20lighting%20and%20clean%20professional%20photography&width=600&height=500&seq=about-story-001&orientation=portrait"
                  alt="Sonoma Routes delivery"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">What Drives Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Our Mission & Values
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-md mb-5">
                <i className="ri-store-2-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Support Local Retail</h3>
              <p className="text-gray-600 leading-relaxed">
                Independent stores are the backbone of our community. We exist to help them compete and thrive with products that sell.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-md mb-5">
                <i className="ri-handshake-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Honest Partnerships</h3>
              <p className="text-gray-600 leading-relaxed">
                No hidden fees, no pressure tactics. We succeed when our partners succeed. It's that simple.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-md mb-5">
                <i className="ri-map-pin-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local First</h3>
              <p className="text-gray-600 leading-relaxed">
                We prioritize Sonoma County producers and retailers. Keeping business local strengthens our entire community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Growing With Sonoma County
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-bold text-gray-200 mb-4">{milestone.year}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                <p className="text-gray-600">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Our Team</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              The People Behind the Routes
            </h2>
            <p className="text-gray-600 mt-4">
              A small, dedicated team committed to reliable service and local partnerships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden">
                <div className="w-full h-64">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">80+</div>
              <p className="text-gray-600">Stores Served</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">15</div>
              <p className="text-gray-600">Local Producers</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">12</div>
              <p className="text-gray-600">Weekly Routes</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">5</div>
              <p className="text-gray-600">Years in Business</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Want to Work With Us?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Whether you're a store looking for products or a producer seeking distribution, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Store Inquiry
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-transparent border-2 border-white text-white text-base font-semibold rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              Producer Inquiry
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
